/**
 * 대기 방출 배관의 반력 계산.
 *
 * 근거: API Standard 520 Part II, 6판(2015) 5.8.2.1 식 (1) — SI
 *
 *   F = 129 × W × √( k·T / ((k+1)·M) ) + (A × P) / 1000
 *
 *   F : 방출점에서의 반력 (N)
 *   W : 기체·증기 유량 (kg/s)          ⚠ kg/h 가 아니다
 *   k : 출구 조건에서의 비열비 Cp/Cv
 *   T : 배관 출구에서의 정체온도 (K)
 *   M : 분자량
 *   A : 방출점 출구 단면적 (mm²)
 *   P : 대기로 팽창하기 직전 출구 배관 내부의 정압 (kPag)
 *
 * 전제(5.8.2.1): 압축성 유체가 **임계 정상유동**으로 엘보와 수직 방출관을
 * 거쳐 대기로 나가는 조건. 운동량과 정압 효과를 모두 포함한다.
 *
 * 정체온도는 대개 알기 어렵다. 표준은 배출온도로 대신해도 좋다고 하되,
 * **반력 산정에는 보수적이지만 다른 배관 설계에는 쓰면 안 된다**고 못박는다
 * (자동냉각·줄–톰슨 효과를 반영하지 못하기 때문).
 */

export interface ReactionForceInput {
  /** W — 유량 (kg/s) */
  readonly massFlowKgS: number;
  /** k — 출구 조건 비열비 */
  readonly specificHeatRatio: number;
  /** T — 배관 출구 정체온도 (K) */
  readonly stagnationTemperatureK: number;
  /** M — 분자량 */
  readonly molecularWeight: number;
  /** A — 방출점 출구 단면적 (mm²) */
  readonly outletAreaMm2: number;
  /** P — 팽창 직전 배관 내부 정압 (kPag) */
  readonly outletStaticPressureKpaGauge: number;
  /** 배출온도를 정체온도 대신 쓴 경우 true — 경고를 띄운다 */
  readonly usedRelievingTemperature?: boolean;
}

export interface ReactionForceSuccess {
  readonly ok: true;
  /** F — 반력 (N) */
  readonly forceN: number;
  /** 운동량 몫 (N) */
  readonly momentumN: number;
  /** 정압 몫 (N) */
  readonly staticN: number;
  readonly equation: "API 520 Part II (1) — SI";
  readonly warnings: readonly string[];
}

export interface ReactionForceFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type ReactionForceResult = ReactionForceSuccess | ReactionForceFailure;

/** 대기 방출 반력 — API 520 Part II 식 (1) SI. */
export function reactionForce(
  input: ReactionForceInput,
): ReactionForceResult {
  const {
    massFlowKgS: W,
    specificHeatRatio: k,
    stagnationTemperatureK: T,
    molecularWeight: M,
    outletAreaMm2: A,
    outletStaticPressureKpaGauge: P,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["유량 W", W],
    ["정체온도 T", T],
    ["분자량 M", M],
    ["출구 단면적 A", A],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return {
        ok: false,
        reason: "invalid-input",
        message: `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`,
      };
    }
  }
  if (!Number.isFinite(k) || k <= 1) {
    return {
      ok: false,
      reason: "invalid-input",
      message: "비열비 k 는 1보다 커야 합니다.",
    };
  }
  if (!Number.isFinite(P) || P < 0) {
    return {
      ok: false,
      reason: "invalid-input",
      message: "출구 정압 P 는 0 이상이어야 합니다(게이지압).",
    };
  }

  const momentumN = 129 * W * Math.sqrt((k * T) / ((k + 1) * M));
  const staticN = (A * P) / 1000;

  const warnings: string[] = [];
  if (input.usedRelievingTemperature === true) {
    warnings.push(
      "정체온도 대신 배출온도를 썼습니다. 반력 산정에는 보수적이지만, " +
        "자동냉각·줄–톰슨 효과를 반영하지 못하므로 **다른 배관 설계 항목에는 " +
        "이 온도를 쓰면 안 됩니다**(API 520 Part II 5.8.2.1).",
    );
  }
  warnings.push(
    "이 식은 압축성 유체가 임계 정상유동으로 엘보와 수직 방출관을 거쳐 " +
      "대기로 나가는 조건을 전제합니다(5.8.2.1). 밀폐 배출계통은 5.8.3 을, " +
      "2상 방출은 5.8.2.2 를 따로 검토해야 합니다.",
  );

  return {
    ok: true,
    forceN: momentumN + staticN,
    momentumN,
    staticN,
    equation: "API 520 Part II (1) — SI",
    warnings,
  };
}
